import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger
} from 'design-system';

import { getSeriesId } from '@/features/charts/lib/seriesVisibility';
import type { ExtendedSeriesOptionsType } from '@/features/charts/types/highcharts';

import useLegendDropdown from './useLegendDropdown';

const LegendItem = ({ series }: { series: ExtendedSeriesOptionsType }) => {
  const unitPrice = `(${series.custom.Currency} / ${series.custom.Units})`;
  const seriesColor =
    (typeof series.color === 'string' && series.color) ||
    (typeof series.custom.color === 'string' && series.custom.color) ||
    'currentColor';

  return (
    <span className='flex min-w-0 items-center gap-1'>
      <span
        aria-hidden='true'
        className='h-0.5 w-2 shrink-0'
        style={{ backgroundColor: seriesColor }}
      />
      <span className='truncate'>
        {series.name} {unitPrice}
      </span>
    </span>
  );
};

const LegendDropdown = ({
  series,
  hiddenSeriesIds,
  setHiddenSeriesIds
}: {
  series: ExtendedSeriesOptionsType[];
  hiddenSeriesIds: string[];
  setHiddenSeriesIds: (ids: string[]) => void;
}) => {
  const {
    selectedSeries,
    selectedSeriesIds,
    allChartSeries,
    handleLegendItemClick,
    isSeriesDisabled
  } = useLegendDropdown({
    series,
    hiddenSeriesIds,
    setHiddenSeriesIds
  });
  const activeSeries = selectedSeries[0];

  return (
    <div className='min-w-[120px] pr-10 pb-4 pl-2'>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              aria-label='Legend series'
              className='w-full min-w-0 justify-start'
              variant='outline'
            >
              {activeSeries ? <LegendItem series={activeSeries} /> : 'Legend series'}
            </Button>
          }
        />
        <DropdownMenuContent align='start'>
          {allChartSeries.map(seriesItem => {
            const seriesId = getSeriesId(seriesItem);
            if (!seriesId) return null;

            return (
              <DropdownMenuCheckboxItem
                key={seriesId}
                checked={selectedSeriesIds.includes(seriesId)}
                disabled={isSeriesDisabled(seriesItem)}
                onCheckedChange={checked => handleLegendItemClick(seriesId, checked)}
              >
                <LegendItem series={seriesItem} />
              </DropdownMenuCheckboxItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default LegendDropdown;
